import { useEffect, useState } from 'react';

// Walter 2026-05-06 round 6: "hide until tomorrow" for overdue
// reminders. Different from snooze (which mutates parsedDate).
// Hide is purely visual + per-device — store a timestamp in
// localStorage; while now < timestamp, the agenda + bell badge
// suppress overdue items. At midnight of tomorrow the timestamp
// expires naturally.
//
// Cross-component reactivity: setters dispatch a custom event so
// every useOverdueHiddenUntil() consumer re-reads. The native
// `storage` event covers cross-tab updates too.

const KEY = 'lists_overdue_hidden_until';
const EVT = 'lists-overdue-hide-changed';

export function getOverdueHiddenUntil() {
  try {
    const v = localStorage.getItem(KEY);
    if (!v) return 0;
    const ts = parseInt(v, 10);
    return Number.isNaN(ts) ? 0 : ts;
  } catch {
    return 0;
  }
}

export function setOverdueHiddenUntil(ts) {
  try {
    if (ts && ts > Date.now()) {
      localStorage.setItem(KEY, String(ts));
    } else {
      localStorage.removeItem(KEY);
    }
    window.dispatchEvent(new CustomEvent(EVT));
  } catch {
    // localStorage disabled — silently no-op. The user just won't
    // get the hide affordance; everything else still works.
  }
}

// Compute the start of tomorrow in local time. Used as the
// "hide until tomorrow" target — overdue items reappear at
// 00:00 of the following day.
export function tomorrowStart() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function isOverdueHidden() {
  const ts = getOverdueHiddenUntil();
  return ts > Date.now();
}

export function useOverdueHiddenUntil() {
  const [ts, setTs] = useState(getOverdueHiddenUntil);
  useEffect(() => {
    const handler = () => setTs(getOverdueHiddenUntil());
    window.addEventListener(EVT, handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener(EVT, handler);
      window.removeEventListener('storage', handler);
    };
  }, []);
  return ts;
}
